/**
 * CrmPipelineInsights — the Tier 2 "real analytics" panel for the pursuit pipeline: win rate,
 * average sales-cycle length, and a stage funnel. Collapsed by default (loads lazily on first
 * expand) since the dated historical numbers start thin — database/116_opportunity_stage_history.sql
 * only exists from 2026-07 forward, no retroactive backfill. The stage funnel and the "all-time"
 * win rate are live snapshots of today's Contract Stage distribution (work from day one); the
 * dated win rate / sales-cycle length come from the history log and are shown honestly as "not
 * enough data yet" until a few deals resolve going forward. See api/opportunity-stage-history.js
 * for the full coverage caveats.
 *
 * Raw vs. competed win rate (2026-07): a firm choosing not to pursue something (No Go by AJA)
 * isn't a competitive loss the way losing to a competitor or the prospect going quiet is —
 * blending it into one denominator understates how the firm performs when it actually competes.
 * Both numbers are shown side by side rather than picking one.
 */
import { useState } from 'react'
import { getPipelineAnalytics } from '../../services/opportunityAnalytics.js'
import { EARLY_STAGES, SYSTEM_STAGES, LOST_STAGES } from '../../services/opportunities.js'

const FUNNEL_ORDER = [...EARLY_STAGES, ...SYSTEM_STAGES, ...LOST_STAGES]

function fmtDate(v) {
  if (!v) return ''
  try { const d = new Date(v); return isNaN(d) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) } catch { return '' }
}
function pct(v) { return v == null ? '—' : `${Math.round(v * 100)}%` }

function Stat({ label, value, hint }) {
  return (
    <div>
      <div style={S.statLabel}>{label}</div>
      <div style={S.statValue}>{value}</div>
      {hint && <div style={S.statHint}>{hint}</div>}
    </div>
  )
}

export default function CrmPipelineInsights() {
  const [open, setOpen] = useState(false)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadedOnce, setLoadedOnce] = useState(false)

  function toggle() {
    const next = !open
    setOpen(next)
    if (next && !loadedOnce) {
      setLoadedOnce(true)
      setLoading(true)
      getPipelineAnalytics().then(setData).catch(() => setData(null)).finally(() => setLoading(false))
    }
  }

  const hist = data && data.historical
  const cur = data && data.currentResolved
  const maxCount = Math.max(1, ...FUNNEL_ORDER.map(s => (data && data.currentFunnel && data.currentFunnel[s]) || 0))

  return (
    <div style={S.wrap}>
      <button style={S.toggle} onClick={toggle}>
        <span style={{ display: 'inline-block', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>▸</span>
        Pipeline Insights
      </button>
      {open && (
        loading ? <div style={S.muted}>Loading…</div>
        : !data ? <div style={S.muted}>Could not load insights.</div>
        : (
          <div style={S.panel}>
            {cur ? (
              <>
                <div style={S.sectionLbl}>All-time (current stages, undated)</div>
                <div style={S.statsRow}>
                  <Stat label="Win rate — raw" value={pct(cur.rawWinRate)}
                    hint={`${cur.won} won of ${cur.won + cur.declinedByUs + cur.lostToOthers} resolved`} />
                  <Stat label="Win rate — competed" value={pct(cur.competedWinRate)}
                    hint={`Excludes ${cur.declinedByUs} AJA declined to pursue`} />
                </div>
              </>
            ) : (
              <div style={S.since}>No resolved opportunities yet — win rate will appear here once a few deals are won or lost.</div>
            )}

            {hist && hist.sampleSize ? (
              <>
                <div style={{ ...S.sectionLbl, marginTop: 14 }}>Tracked since {fmtDate(hist.oldestEntryAt)}</div>
                <div style={S.statsRow}>
                  <Stat label="Win rate — raw" value={pct(hist.rawWinRate)} hint={`${hist.sampleSize} resolved, dated`} />
                  <Stat label="Win rate — competed" value={pct(hist.competedWinRate)} hint="Excludes AJA declines" />
                  <Stat label="Avg. sales cycle" value={hist.avgCycleDays != null ? `${hist.avgCycleDays}d` : '—'} hint="Created to Executed" />
                </div>
              </>
            ) : (
              <div style={S.since}>No dated stage-history yet — a trend-over-time version of these numbers (plus average sales-cycle length) will appear here as deals resolve going forward.</div>
            )}

            <div style={S.funnelHead}>Current pipeline snapshot</div>
            {FUNNEL_ORDER.map(s => {
              const count = (data.currentFunnel && data.currentFunnel[s]) || 0
              return (
                <div key={s} style={S.funnelRow}>
                  <span style={S.funnelLabel}>{s}</span>
                  <div style={S.funnelBarWrap}><div style={{ ...S.funnelBar, width: `${(count / maxCount) * 100}%` }} /></div>
                  <span style={S.funnelCount}>{count}</span>
                </div>
              )
            })}
          </div>
        )
      )}
    </div>
  )
}

const S = {
  wrap: { marginBottom: 14 },
  toggle: { display: 'flex', alignItems: 'center', gap: 7, fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: 600, color: 'var(--aj-text-mid)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 2px' },
  muted: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', fontStyle: 'italic', padding: '8px 2px' },
  panel: { background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 10, padding: '14px 16px', marginTop: 8 },
  sectionLbl: { fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', marginBottom: 8 },
  statsRow: { display: 'flex', gap: 24, marginBottom: 10, flexWrap: 'wrap' },
  statLabel: { fontFamily: '"DM Sans", sans-serif', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', marginBottom: 4 },
  statValue: { fontFamily: '"DM Mono", monospace', fontSize: 20, fontWeight: 500, color: 'var(--aj-text)' },
  statHint: { fontFamily: '"DM Sans", sans-serif', fontSize: 10.5, color: 'var(--aj-text-mid)', marginTop: 2 },
  since: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', fontStyle: 'italic', marginBottom: 12, lineHeight: 1.5 },
  funnelHead: { fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', borderTop: '0.5px solid var(--aj-border)', paddingTop: 10, marginTop: 14, marginBottom: 6 },
  funnelRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' },
  funnelLabel: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', width: 168, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  funnelBarWrap: { flex: 1, height: 8, background: 'var(--aj-bg-muted)', borderRadius: 4, overflow: 'hidden' },
  funnelBar: { height: '100%', background: '#2B7A9E', borderRadius: 4, minWidth: 2 },
  funnelCount: { fontFamily: '"DM Mono", monospace', fontSize: 11.5, color: 'var(--aj-text)', width: 24, textAlign: 'right', flexShrink: 0 },
}
